import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Drawer, Link, Image, Portal, useSlotRecipe, Text, Button} from '@chakra-ui/react3'
import {useForm} from 'react-hook-form'
import {useIntl} from 'react-intl'
import {useSearchSuggestions} from '@salesforce/commerce-sdk-react'
import debounce from 'lodash/debounce'
import {
    RECENT_SEARCH_KEY,
    RECENT_SEARCH_LIMIT,
    RECENT_SEARCH_MIN_LENGTH
} from '../../../../constants'
import useEffectEvent from '../../../../hooks/use-effect-event'
import useNavigation from '../../../../hooks/use-navigation'
import {trackEvent} from '../../../../tracking/analytics'
import {productUrlBuilder, searchUrlBuilder} from '../../../../utils/url'
import {clearSessionJSONItem, getSessionJSONItem, setSessionJSONItem} from '../../../../utils/utils'
import InputField, {InputToken} from '../../input-field'
import {ArrowRightIcon, CancelIcon, SearchIcon} from '../../icons'
import {searchOverlayRecipe} from './search-overlay.recipe'
import NavigationSeparator from '../navigation-separator'

const SEARCH_BAR_POSITION = 'Top'

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const ResultText = ({label, searchValue}) => {
    const recipe = useSlotRecipe({recipe: searchOverlayRecipe})
    const styles = useMemo(() => recipe(), [recipe])
    const searchTerm = searchValue.trim()

    if (!searchTerm) {
        return label
    }

    const searchTermPattern = new RegExp(`(${escapeRegExp(searchTerm)})`, 'gi')

    return (
        <Text css={styles.resultText}>
            {label.split(searchTermPattern).map((segment, index) =>
                segment.toLowerCase() === searchTerm.toLowerCase() ? (
                    <Box as="mark" css={styles.resultHighlight} key={index}>
                        {segment}
                    </Box>
                ) : (
                    segment
                )
            )}
        </Text>
    )
}

ResultText.propTypes = {
    label: PropTypes.string.isRequired,
    searchValue: PropTypes.string.isRequired
}

/**
 * Lists session-scoped search terms and actions for selecting or clearing them.
 *
 * @param {Object} props - Component properties.
 * @param {string[]} props.searches - Stored search terms, ordered newest first.
 * @param {Function} props.onSelect - Selects a stored search term.
 * @param {Function} props.onClear - Clears the stored search terms.
 * @returns {JSX.Element} The recent-search list.
 */
const RecentSearches = ({searches, noResultsMessage, onSelect, onClear}) => {
    const {formatMessage} = useIntl()
    const recipe = useSlotRecipe({recipe: searchOverlayRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    return (
        <Box css={styles.contentContainer}>
            {noResultsMessage && (
                <>
                    <Box css={styles.noResultContainer}>
                        <SearchIcon css={styles.searchIcon} />
                        <Text role="status">{noResultsMessage}</Text>
                    </Box>
                    <NavigationSeparator variant="divider" />
                </>
            )}
            <Box css={styles.recentSearches}>
                <Text css={styles.contentLabel}>
                    {formatMessage({
                        id: 'recent_searches.heading.recent_searches',
                        defaultMessage: 'Recent Searches'
                    })}
                </Text>
                {searches.map((search) => (
                    <Box
                        as="button"
                        css={styles.contentItem}
                        key={search}
                        type="button"
                        onClick={() => onSelect(search)}
                    >
                        <SearchIcon css={styles.searchIcon} />
                        <Text css={styles.contentItemLabel}>{search}</Text>
                    </Box>
                ))}
            </Box>
            <NavigationSeparator variant="divider" />
            <Link
                as="button"
                type="button"
                variant="primary"
                size="small"
                underline
                css={styles.clearRecentSearchLink}
                onClick={onClear}
            >
                <span>
                    {formatMessage({
                        id: 'recent_searches.action.clear_searches',
                        defaultMessage: 'Clear recent searches'
                    })}
                </span>
            </Link>
        </Box>
    )
}

RecentSearches.propTypes = {
    searches: PropTypes.arrayOf(PropTypes.string).isRequired,
    noResultsMessage: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired
}

const SearchResults = ({searchResults, searchValue, close, onViewAll}) => {
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const recipe = useSlotRecipe({recipe: searchOverlayRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    const selectResult = (result) => {
        trackEvent({
            event: 'search',
            search_bar_position: SEARCH_BAR_POSITION,
            search_query: searchValue,
            flyout_section: 'Products',
            flyout_selection: result.label
        })
        navigate(result.href)
        close()
    }

    return (
        <Box css={styles.contentContainer}>
            <Box css={styles.contentItem}>
                <SearchIcon css={styles.searchIcon} />
                <Text css={styles.contentItemLabel}>{searchValue}</Text>
            </Box>
            <NavigationSeparator variant="divider" />
            <Box>
                <Text css={styles.contentLabel}>
                    {formatMessage({
                        id: 'global.products',
                        defaultMessage: 'Products'
                    })}
                </Text>
                {searchResults.map((result) => (
                    <Box
                        as="button"
                        css={styles.result}
                        className="leap-search-overlay-result"
                        key={result.href}
                        type="button"
                        onClick={() => selectResult(result)}
                    >
                        {result.image ? (
                            <Image src={result.image} alt="" css={styles.resultImage} />
                        ) : null}
                        <ResultText label={result.label} searchValue={searchValue} />
                    </Box>
                ))}
            </Box>
            <NavigationSeparator variant="divider" />
            <Link
                as="button"
                type="button"
                variant="primary"
                size="small"
                underline
                css={styles.viewAllLink}
                onClick={() => onViewAll(searchValue)}
            >
                <span>
                    {formatMessage({
                        id: 'search.view_all',
                        defaultMessage: 'View all'
                    })}
                </span>
                <ArrowRightIcon />
            </Link>
        </Box>
    )
}

SearchResults.propTypes = {
    searchResults: PropTypes.arrayOf(
        PropTypes.shape({
            href: PropTypes.string.isRequired,
            image: PropTypes.string,
            label: PropTypes.string.isRequired
        })
    ).isRequired,
    searchValue: PropTypes.string.isRequired,
    close: PropTypes.func.isRequired,
    onViewAll: PropTypes.func.isRequired
}

const SearchForm = ({
    searchValue,
    control,
    close,
    onSubmit,
    onSearchChange,
    clearSearch,
    expanded,
    closeVisible,
    onSearchTransitionEnd
}) => {
    const {formatMessage} = useIntl()
    const closeLabel = formatMessage({
        id: 'global.close',
        defaultMessage: 'Close'
    })
    const recipe = useSlotRecipe({recipe: searchOverlayRecipe})
    const styles = useMemo(() => recipe({expanded, closeVisible}), [recipe, expanded, closeVisible])

    return (
        <Box as="form" css={styles.form} onSubmit={onSubmit}>
            <Box css={styles.search} onTransitionEnd={onSearchTransitionEnd}>
                <InputField
                    name="search"
                    control={control}
                    type="search"
                    label={formatMessage({
                        id: 'global.search',
                        defaultMessage: 'Search'
                    })}
                    hideLabel
                    placeholder={formatMessage({
                        id: 'global.search',
                        defaultMessage: 'Search'
                    })}
                    variant="filled"
                    value={searchValue}
                    onChange={onSearchChange}
                    css={styles.searchInput}
                    startOffset="0.75rem"
                    startElement={
                        <InputToken
                            srLabel={formatMessage({
                                id: 'global.search',
                                defaultMessage: 'Search'
                            })}
                        >
                            <SearchIcon css={styles.searchIcon} />
                        </InputToken>
                    }
                    endElementProps={styles.searchEndElement}
                    endElement={({value}) =>
                        value ? (
                            <Button
                                size="sm-icon-only"
                                variant="ghost"
                                aria-label={formatMessage({
                                    id: 'product_list.search.delete_input',
                                    defaultMessage: 'Delete input'
                                })}
                                onClick={clearSearch}
                            >
                                <InputToken>
                                    <CancelIcon />
                                </InputToken>
                            </Button>
                        ) : null
                    }
                />
            </Box>
            <Link
                as="button"
                type="button"
                variant="secondary"
                underline={false}
                css={styles.closeButton}
                aria-label={closeLabel}
                onClick={close}
            >
                <span>{closeLabel}</span>
            </Link>
        </Box>
    )
}

SearchForm.propTypes = {
    searchValue: PropTypes.string.isRequired,
    control: PropTypes.object.isRequired,
    close: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    clearSearch: PropTypes.func.isRequired,
    expanded: PropTypes.bool.isRequired,
    closeVisible: PropTypes.bool.isRequired,
    onSearchTransitionEnd: PropTypes.func.isRequired
}

/**
 * Search overlay presented from the Xleap header.
 *
 * Renders the Xleap filled search field inside a controlled drawer managed by
 * the application overlay manager.
 *
 * @component
 * @param {Object} props - Overlay manager properties.
 * @param {boolean} props.open - Whether the drawer is open.
 * @param {Function} props.onOpenChange - Signals a requested open-state change.
 * @param {Function} props.onExitComplete - Signals that the exit animation completed.
 * @returns {JSX.Element}
 */
const SearchOverlay = ({open, onOpenChange, onExitComplete, portalContainer}) => {
    const {formatMessage, locale} = useIntl()
    const [searchValue, setSearchValue] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [searchInputExpanded, setSearchInputExpanded] = useState(false)
    const [searchCloseVisible, setSearchCloseVisible] = useState(false)
    const [recentSearches, setRecentSearches] = useState(
        () => getSessionJSONItem(RECENT_SEARCH_KEY) || []
    )
    const navigate = useNavigation()
    const recipe = useSlotRecipe({recipe: searchOverlayRecipe})
    const styles = useMemo(() => recipe(), [recipe])
    const {control, handleSubmit, reset} = useForm({
        defaultValues: {
            search: ''
        }
    })
    const searchSuggestion = useSearchSuggestions(
        {
            parameters: {
                q: searchQuery,
                limit: 3
            }
        },
        {
            enabled: searchQuery?.length >= RECENT_SEARCH_MIN_LENGTH,
            keepPreviousData: true
        }
    )
    const searchResults = useMemo(() => {
        if (searchValue.trim().length < RECENT_SEARCH_MIN_LENGTH) {
            return []
        }

        return (
            searchSuggestion.data?.productSuggestions?.products
                ?.filter((product) => product.productName)
                .map((product) => ({
                    label: product.productName,
                    href: productUrlBuilder(product, locale),
                    image: product.c_image
                })) || []
        )
    }, [locale, searchSuggestion.data, searchValue])
    const hasNoResults =
        searchValue.trim().length >= RECENT_SEARCH_MIN_LENGTH &&
        searchQuery === searchValue &&
        searchSuggestion.isSuccess &&
        !searchSuggestion.isFetching &&
        !searchResults.length
    const noResultsMessage = hasNoResults
        ? formatMessage(
              {
                  id: 'search_results.info.no_results_for',
                  defaultMessage: 'No results for "{searchedValue}"'
              },
              {searchedValue: searchValue.trim()}
          )
        : null
    const debouncedSearch = useMemo(() => debounce(setSearchQuery, 300), [])
    const onSearchQueryChange = useEffectEvent((query) => {
        trackEvent({
            event: 'search',
            search_bar_position: SEARCH_BAR_POSITION,
            search_query: query
        })
    })

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

    useEffect(() => {
        if (!open) {
            setSearchInputExpanded(false)
            setSearchCloseVisible(false)
            return undefined
        }

        setSearchCloseVisible(false)
        const frame = requestAnimationFrame(() => setSearchInputExpanded(true))
        return () => cancelAnimationFrame(frame)
    }, [open])

    useEffect(() => {
        if (searchQuery) {
            onSearchQueryChange(searchQuery)
        }
    }, [searchQuery, onSearchQueryChange])

    const onSearchChange = (event) => {
        const input = event.target.value

        setSearchValue(input)

        if (input.length >= RECENT_SEARCH_MIN_LENGTH) {
            debouncedSearch(input)
        }
    }

    const clearSearch = () => {
        reset({search: ''})
        setSearchValue('')
        setSearchQuery('')
        debouncedSearch.cancel()
    }

    const saveRecentSearch = (searchText) => {
        const searches = [
            searchText,
            ...recentSearches.filter(
                (savedSearchTerm) => searchText.toLowerCase() !== savedSearchTerm.toLowerCase()
            )
        ].slice(0, RECENT_SEARCH_LIMIT)

        setSessionJSONItem(RECENT_SEARCH_KEY, searches)
        setRecentSearches(searches)
    }

    const close = () => onOpenChange({open: false})
    const selectRecentSearch = (search) => {
        trackEvent({
            event: 'search',
            search_query: searchValue
        })
        close()
        navigate(searchUrlBuilder(search))
    }
    const clearRecentSearches = () => {
        clearSessionJSONItem(RECENT_SEARCH_KEY)
        setRecentSearches([])
        clearSearch()
        close()
    }
    const viewAllResults = (searchValue) => {
        const query = searchValue.trim()

        if (!query) {
            return
        }
        saveRecentSearch(query)
        clearSearch()
        close()
        navigate(searchUrlBuilder(query))
    }
    const onDrawerOpenChange = (details) => onOpenChange(details)
    const onSearchTransitionEnd = (event) => {
        if (open && event.target === event.currentTarget && event.propertyName === 'width') {
            setSearchCloseVisible(true)
        }
    }
    const onSubmit = handleSubmit(({search}) => viewAllResults(search))

    return (
        <Drawer.Root
            open={open}
            placement="top"
            onOpenChange={onDrawerOpenChange}
            onExitComplete={onExitComplete}
        >
            <Portal container={portalContainer}>
                <Drawer.Backdrop css={styles.backdrop} />
                <Drawer.Positioner css={styles.positioner}>
                    <Drawer.Content
                        css={styles.content}
                        aria-label={formatMessage({
                            id: 'header.search.assistive_msg.toolbar.label',
                            defaultMessage: 'Search toolbar'
                        })}
                    >
                        <Drawer.Body css={styles.body}>
                            <SearchForm
                                searchValue={searchValue}
                                control={control}
                                close={close}
                                onSubmit={onSubmit}
                                onSearchChange={onSearchChange}
                                clearSearch={clearSearch}
                                expanded={searchInputExpanded}
                                closeVisible={searchCloseVisible}
                                onSearchTransitionEnd={onSearchTransitionEnd}
                            />
                            {searchResults.length ? (
                                <SearchResults
                                    searchResults={searchResults}
                                    searchValue={searchValue}
                                    close={close}
                                    onViewAll={viewAllResults}
                                />
                            ) : recentSearches.length ? (
                                <RecentSearches
                                    searches={recentSearches}
                                    noResultsMessage={noResultsMessage}
                                    onSelect={selectRecentSearch}
                                    onClear={clearRecentSearches}
                                />
                            ) : noResultsMessage ? (
                                <Box css={styles.contentContainer}>
                                    <Box css={styles.noResultContainer}>
                                        <SearchIcon css={styles.searchIcon} />
                                        <Text role="status">
                                            <span>{noResultsMessage}</span>
                                        </Text>
                                    </Box>
                                </Box>
                            ) : null}
                        </Drawer.Body>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    )
}

SearchOverlay.propTypes = {
    open: PropTypes.bool,
    onOpenChange: PropTypes.func.isRequired,
    onExitComplete: PropTypes.func,
    portalContainer: PropTypes.object
}

export default SearchOverlay
